<template>
  <MarktetCard :class="['win-draw-win', classes.marketCard, layout, template]">
    <CardHeader :eventId="eventId" />

    <div :class="classes.eventMarketWrapper">
      <MarketName
        :class="classes.market"
        :marketId="marketId"
        class="market-name"
      />
    </div>

    <div :class="classes.optionWrapper">
      <EventName
        :class="[classes.eventName, 'event-name']"
        :marketId="marketId"
        :eventId="eventId"
        :useTeamNames="!useTeamIcons"
      />
      <div :class="classes.container">
        <div v-if="useTeamIcons" :class="classes.competitors">
          <WinCompetitor
            :eventId="eventId"
            :class="classes.competitor"
            :team="competitors[0].name.short"
          />
          <DrawText
            :eventId="eventId"
            :class="[classes.competitor, classes.draw]"
          />
          <WinCompetitor
            :class="classes.competitor"
            :eventId="eventId"
            :team="competitors[1].name.short"
          />
        </div>

        <div :class="classes.buttons">
          <Button
            :stagger="0"
            :class="classes.button"
            :optionId="homeAwayOptions[0].options[0]"
            :marketId="homeAwayOptions[0].marketId"
          />
          <Button
            :stagger="1"
            :class="[classes.button, classes.draw]"
            :optionId="homeAwayOptions[0].options[1]"
            :marketId="homeAwayOptions[0].marketId"
            :isDraw="true"
          />
          <Button
            :stagger="2"
            :class="classes.button"
            :optionId="homeAwayOptions[0].options[2]"
            :marketId="homeAwayOptions[0].marketId"
          />
        </div>
      </div>
    </div>
  </MarktetCard>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, ComputedRef, defineComponent, ref } from "vue";
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";

import MarketName from "../../../MarketName.vue";
import CardHeader from "../../../CardHeader.vue";
import Button from "../Button.vue";
import WinCompetitor from "../../../WinCompetitor.vue";
import DrawText from "./DrawText.vue";
import MarktetCard from "../../../styled/MarketCard.vue";

import EventName from "../../../EventNameBadges.vue";

import {
  composeEventCompetitors,
  composeHomeAwayOptions
} from "@fresh8/ad-data-composition/src/betData";
import { injectCarouselContext } from "../../../../lib/provideInjectors";
import {
  injectTemplateSize,
  LARGE_RESPONSIVE
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { injectLayout, SQUARISH } from "../../../../hooks/useLayout";
import {
  injectTeamColors,
  injectUseTeamBadges
} from "../../../../lib/teamBranding";
import { injectUseTeamJerseys } from "../../../../lib/teamJerseys";
import { SportCompetitor } from "@fresh8/types/src/betData";
import { areColorsAvailable } from "@fresh8/ad-data-composition/src/lib/setBannerColors";

const useStyles = createUseStyles({
  market: {
    padding: "0"
  },

  eventMarketWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "8px 0px"
  },

  optionWrapper: {
    position: "relative",
    boxSizing: "border-box",
    display: "grid",
    height: "100%",
    minHeight: "0px",
    gridTemplateColumns: props =>
      `1fr 1fr 1fr ${props.isCarousel ? "35px" : "40px"} 1fr 1fr 1fr`,
    marginBottom: "8px",
    lineHeight: 1
  },
  eventName: {
    alignContent: "center",
    gridColumn: "1 / -1",
    margin: "0",

    "&.event-name": {
      gridTemplateColumns: `1fr 1fr 1fr repeat(2, 30px) 1fr 1fr 1fr`,
      marginBottom: "8px"
    },
    "& .competitors .banner": {
      maxWidth: "unset"
    },

    "& .competitors": {
      "&:first-of-type": {
        gridColumn: "1 / 5"
      },
      "&:last-of-type": { gridColumn: "5 / -1" },

      "& .banner": {
        height: "40px",
        maxWidth: "unset",
        "& .team-name": {
          fontSize: "12px",
          lineHeight: 1.2
        },
        "&.right": {
          marginLeft: props => (props.isCarousel ? "15%" : "30px")
        },
        "&.left": {
          marginRight: props => (props.isCarousel ? "15%" : "30px")
        }
      }
    },

    "& .banner .badge": {
      maxWidth: "unset"
    },
    "& .banner.right .badge, & .banner.left .badge": {
      width: "64px"
    },

    "& .banner .jersey": {
      maxWidth: "unset"
    },
    "& .banner.right .jersey, & .banner.left .jersey": {
      width: "64px"
    },

    "& .vs": {
      alignItems: "center",
      height: "100%"
    },
    "& .helmet": {
      position: "relative",
      top: "-2px"
    }
  },
  competitors: {
    boxSizing: "border-box",
    display: "flex",

    flexDirection: "row",
    flex: "1 0 auto",
    minHeight: "28px",
    height: "auto"
  },
  competitor: {
    boxSizing: "border-box",
    flex: "1 1 40%",
    fontSize: "11px",
    textAlign: "center",
    minHeight: "0px",
    width: "100%",
    height: "auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    verticalAlign: "middle",
    margin: "8px 0"
  },
  draw: {
    display: "flex",
    flex: "1 1 30% !important",
    margin: "0 6px"
  },
  buttons: {
    boxSizing: "border-box",
    display: "flex",
    flex: "1 1 100%",
    height: "100%",
    minHeight: "0px",
    flexDirection: "row",
    alignItems: "flex-end"
  },

  container: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gridColumn: "1 / -1",
    width: "100%",
    height: "100%",
    minHeight: "0px",
    padding: "0 8px"
  },
  button: {
    boxSizing: "border-box",
    flex: "1 1 40%",
    minHeight: "36px",
    maxHeight: "53px",

    "& .button": {
      alignItems: "center",
      justifyContent: "center",
      padding: "0",
      height: "100%",

      "& div": {
        margin: "0"
      }
    },

    "& div": {
      textAlign: "center"
    },
    "& .name": {
      display: "none"
    }
  },
  marketCard: {
    [`&.${LARGE_RESPONSIVE.name}`]: {
      "& $market": {
        margin: "6px 0",
        fontSize: "11px"
      },
      "& $eventName": {
        "& .banner .team-name": {
          fontSize: "14px"
        }
      },
      [`&.${SQUARISH}`]: {
        "& $eventName": {
          "& .banner.right .badge, & .banner.left .badge": {
            margin: "0"
          },
          "& .banner .team-name": {
            fontSize: "12px"
          }
        }
      }
    }
  }
});

export default defineComponent({
  name: "Card",
  components: {
    CardHeader,
    MarketName,
    Button,
    MarktetCard,
    WinCompetitor,
    DrawText,
    EventName
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const {
      adTemplateBranding,
      brand,
      marketBranding
    } = injectBrand() as BrandContext;
    const template = injectTemplateSize();
    const { layout } = injectLayout();
    const teamColorsContext = injectTeamColors();

    const competitors = composeEventCompetitors(props.eventId) as ComputedRef<
      SportCompetitor[]
    >;

    const useTeamIcons = computed(() => {
      return teamColorsContext.teamIconsAvailable.value;
    });

    const marketId = ref([props.marketId]);
    const carouselContext = injectCarouselContext();
    const reactiveStyles = computed(() => ({
      ...adTemplateBranding.value,
      isCarousel: carouselContext.value.isCarousel
    }));

    const classesRef = useStyles(reactiveStyles);
    const homeAwayOptions = composeHomeAwayOptions(marketId);

    return {
      layout,
      template: computed(() => template?.size.value.name),
      competitors,
      marketBranding,
      classes: classesRef,
      homeAwayOptions,
      useTeamIcons
    };
  }
});
</script>
